import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Calendar, Tag, MessageCircle, BookOpen } from 'lucide-react';
import SEOHead from '../components/SEOHead';
import { SchemaScript, generateBlogPostingSchema, generateBreadcrumbSchema } from '../utils/seoSchema';
import { getBlogPostBySlug } from '../utils/api';

const formatDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  } catch (_e) {
    return iso;
  }
};

const HeyTaxiPost = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setNotFound(false);
    (async () => {
      try {
        const data = await getBlogPostBySlug(slug);
        if (!cancelled) setPost(data);
      } catch (err) {
        if (!cancelled) {
          if (err?.response?.status === 404) setNotFound(true);
          else console.error('Failed to load post:', err);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [slug]);

  if (loading) {
    return (
      <main className="bg-white min-h-screen pt-28 pb-16">
        <p className="text-center text-gray-500 py-16" data-testid="hey-taxi-post-loading">Loading article…</p>
      </main>
    );
  }

  if (notFound || !post) {
    return (
      <>
        <SEOHead title="Article Not Found — Hey Taxi! | Taxi-98" canonical="/hey-taxi" />
        <main className="bg-white min-h-screen pt-32 pb-20" data-testid="hey-taxi-post-not-found">
          <div className="max-w-2xl mx-auto px-4 sm:px-6 text-center">
            <BookOpen className="w-14 h-14 mx-auto text-gray-300 mb-4" />
            <h1 className="text-3xl font-bold text-gray-900 mb-3">Article not found</h1>
            <p className="text-gray-600 mb-8">
              The article you&rsquo;re looking for may have been moved or unpublished.
            </p>
            <Link
              to="/hey-taxi"
              className="inline-flex items-center gap-2 bg-cyan-600 hover:bg-cyan-700 text-white px-6 py-3 rounded-lg font-semibold transition"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Hey Taxi!
            </Link>
          </div>
        </main>
      </>
    );
  }

  return (
    <>
      <SEOHead
        title={post.seoTitle || `${post.title} — Hey Taxi! | Taxi-98`}
        description={post.seoDescription || post.excerpt || ''}
        canonical={`/hey-taxi/${post.slug}`}
        ogType="article"
      />
      <SchemaScript schema={generateBlogPostingSchema(post)} />
      <SchemaScript
        schema={generateBreadcrumbSchema([
          { name: 'Home', url: 'https://taxi-98.com/' },
          { name: 'Hey Taxi!', url: 'https://taxi-98.com/hey-taxi' },
          { name: post.title, url: `https://taxi-98.com/hey-taxi/${post.slug}` },
        ])}
      />

      <main className="bg-white min-h-screen" data-testid="hey-taxi-post-page">
        {/* Header */}
        <section className="pt-28 pb-10 sm:pt-32 sm:pb-12 bg-gradient-to-br from-gray-950 via-slate-900 to-gray-950 text-white">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <button
              type="button"
              onClick={() => navigate('/hey-taxi')}
              data-testid="hey-taxi-post-back"
              className="inline-flex items-center gap-2 text-cyan-300 hover:text-cyan-200 text-sm font-semibold mb-6 transition"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Hey Taxi!
            </button>
            <div className="flex flex-wrap items-center gap-3 text-xs sm:text-sm text-gray-300 mb-5">
              <span className="inline-flex items-center gap-1 bg-cyan-400/10 border border-cyan-300/30 text-cyan-200 px-2.5 py-1 rounded-full font-semibold uppercase tracking-[0.16em] text-[10px]">
                <Tag className="w-3 h-3" />
                {post.category}
              </span>
              <span className="inline-flex items-center gap-1">
                <Calendar className="w-3.5 h-3.5" />
                {formatDate(post.publishedAt || post.createdAt)}
              </span>
            </div>
            <h1
              className="text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight tracking-tight"
              data-testid="hey-taxi-post-title"
            >
              {post.title}
            </h1>
          </div>
        </section>

        {/* Optional hero image */}
        {post.imageUrl && (
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 -mt-6 sm:-mt-8 relative z-10">
            <img
              src={post.imageUrl}
              alt={post.imageAlt || post.title}
              className="w-full aspect-[16/9] object-cover rounded-2xl shadow-xl border border-white/40"
              loading="eager"
              data-testid="hey-taxi-post-image"
            />
          </div>
        )}
        {!post.imageUrl && (
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 -mt-6 sm:-mt-8 relative z-10">
            <div
              className="w-full aspect-[16/9] rounded-2xl shadow-xl border border-white/40 bg-gradient-to-br from-cyan-100 via-blue-100 to-amber-100 flex items-center justify-center"
              data-testid="hey-taxi-post-image-placeholder"
            >
              <BookOpen className="w-20 h-20 text-cyan-700/30" />
            </div>
          </div>
        )}

        {/* Body */}
        <article className="py-12 sm:py-16">
          <div
            data-testid="hey-taxi-post-body"
            className="hey-taxi-post-body max-w-3xl mx-auto px-4 sm:px-6 lg:px-8"
            dangerouslySetInnerHTML={{ __html: post.body || '' }}
          />
        </article>

        {/* Bottom CTA */}
        <section
          className="py-14 sm:py-16 bg-gradient-to-br from-cyan-50 via-white to-amber-50 border-t border-gray-100"
          data-testid="hey-taxi-post-cta"
        >
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3">
              Need reliable transportation in Nassau? Book your Taxi-98 ride today.
            </h2>
            <p className="text-gray-600 mb-7">
              Airport pickups, hotel transfers, cruise port rides, private hire, and tours — pre-book in a few minutes.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4">
              <Link
                to="/book"
                data-testid="hey-taxi-post-book-btn"
                className="inline-flex items-center justify-center gap-2 bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 text-white px-7 py-3.5 rounded-lg font-bold text-base shadow-lg transition"
              >
                <span>Book a Ride</span>
                <ArrowRight className="w-4 h-4" />
              </Link>
              <a
                href="https://wa.me/12428107335?text=Hello%20Taxi-98%2C%20I%20would%20like%20to%20book%20a%20ride."
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hey-taxi-post-whatsapp-btn"
                className="inline-flex items-center justify-center gap-2 bg-green-500 hover:bg-green-600 text-white px-7 py-3.5 rounded-lg font-bold text-base shadow-lg transition"
              >
                <MessageCircle className="w-4 h-4" />
                <span>WhatsApp Taxi-98</span>
              </a>
            </div>
          </div>
        </section>
      </main>
    </>
  );
};

export default HeyTaxiPost;
